/**
 * Change-password form — shared by the voluntary `/preferences` Security
 * section (`SecurityPage`) and the forced full-screen gate
 * (`ForcePasswordChangePage`). Both hit the same
 * `POST /py-api/v1/auth/change-password`.
 */
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Field, Input, Label } from "@/components/ui/Input";
import { changePassword, SecurityApiError } from "./api";

const MIN_LENGTH = 8;

function strengthHint(password: string): { label: string; tone: "weak" | "fair" | "strong" } {
  if (password.length === 0) return { label: "", tone: "weak" };
  let score = 0;
  if (password.length >= MIN_LENGTH) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (score <= 2) return { label: "Weak", tone: "weak" };
  if (score <= 3) return { label: "Fair", tone: "fair" };
  return { label: "Strong", tone: "strong" };
}

const toneClass: Record<string, string> = {
  weak: "text-[var(--cp-rose-ink)]",
  fair: "text-[var(--cp-amber-ink)]",
  strong: "text-[var(--cp-emerald)]",
};

interface ChangePasswordFormProps {
  onSuccess: () => void;
  /** Hidden on the forced gate — there is nowhere to cancel TO. */
  onCancel?: () => void;
  submitLabel?: string;
}

export function ChangePasswordForm({ onSuccess, onCancel, submitLabel = "Change password" }: ChangePasswordFormProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = strengthHint(newPassword);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  const samePassword = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= MIN_LENGTH &&
    !mismatch &&
    !samePassword &&
    !busy;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess();
    } catch (err) {
      // Server message shown verbatim — this form does not guess whether it
      // was a wrong current password, a reused password, or a policy miss.
      setError(err instanceof SecurityApiError ? err.message : "Could not change your password. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-[9px] border border-[var(--cp-rose-ink)]/30 bg-[var(--cp-rose-wash)] p-3 text-sm text-[var(--cp-rose-ink)]">
          {error}
        </div>
      )}

      <Field>
        <Label htmlFor="current-password" required>
          Current password
        </Label>
        <div className="relative">
          <Input
            id="current-password"
            type={showCurrent ? "text" : "password"}
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowCurrent((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--cp-ink-3)] hover:text-[var(--cp-ink)]"
            aria-label={showCurrent ? "Hide current password" : "Show current password"}
            tabIndex={-1}
          >
            {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </Field>

      <Field>
        <Label htmlFor="new-password" required hint={`At least ${MIN_LENGTH} characters`}>
          New password
        </Label>
        <div className="relative">
          <Input
            id="new-password"
            type={showNew ? "text" : "password"}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="pr-10"
            required
            minLength={MIN_LENGTH}
          />
          <button
            type="button"
            onClick={() => setShowNew((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--cp-ink-3)] hover:text-[var(--cp-ink)]"
            aria-label={showNew ? "Hide new password" : "Show new password"}
            tabIndex={-1}
          >
            {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {newPassword.length > 0 && (
          <p className={`mt-1 text-xs font-medium ${toneClass[strength.tone]}`}>
            {strength.label} password
          </p>
        )}
        {samePassword && (
          <p className="mt-1 text-xs text-[var(--cp-amber-ink)]">
            New password must be different from your current one.
          </p>
        )}
      </Field>

      <Field>
        <Label htmlFor="confirm-password" required>
          Confirm new password
        </Label>
        <Input
          id="confirm-password"
          type={showNew ? "text" : "password"}
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {mismatch && <p className="mt-1 text-xs text-[var(--cp-rose-ink)]">Passwords don't match.</p>}
        {tooShort && !mismatch && (
          <p className="mt-1 text-xs text-[var(--cp-ink-3)]">
            {MIN_LENGTH - newPassword.length} more character{MIN_LENGTH - newPassword.length === 1 ? "" : "s"} needed.
          </p>
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={!canSubmit} loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
